import { ChildProviderProps } from "@/types";
import { ChatContextType, Message } from "@/types/chat";
import { ChatMode } from "@/components/elements/chat-mode-selector";
import { useEffect, useRef, useState, createContext, useContext } from "react";
import { useParams } from "react-router";

// Simple context creation
const ChatContext = createContext<ChatContextType | undefined>(undefined);

// DEF-03: ChatProvider is mounted per-Chat-page, so its state dies on
// nav to Dashboard/etc. Persist the mode in localStorage so returning to
// AI Chat keeps whatever the user last chose (e.g. Design Mode) instead of
// falling back to the default.
//
// Keyed by project id so each project remembers its own preference — a mode
// picked on project A shouldn't be forced onto project B when they open it.
const chatModeStorageKey = (projectId: string): string =>
  `chat.chatMode.${projectId || "_"}`;

const readStoredChatMode = (projectId: string): ChatMode => {
  try {
    const stored = localStorage.getItem(chatModeStorageKey(projectId));
    if (
      stored &&
      (Object.values(ChatMode) as string[]).includes(stored)
    ) {
      return stored as ChatMode;
    }
  } catch {
    // localStorage may throw in private-mode / sandbox contexts. Ignore.
  }
  return ChatMode.GENERAL_V3;
};

// Simple provider - just manages messages and chat mode
export const ChatProvider: React.FC<ChildProviderProps> = ({ children }) => {
  const { id = "" } = useParams<{ id: string }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatMode, setChatMode] = useState<ChatMode>(() =>
    readStoredChatMode(id),
  );

  // Re-hydrate when the project changes without remounting the provider
  // (e.g. React Router keeps the Chat page mounted when the `:id` param
  // changes). Without this the first project's mode would leak into the
  // second project's session until the tab is reloaded.
  const previousIdRef = useRef(id);
  useEffect(() => {
    if (previousIdRef.current === id) return;
    previousIdRef.current = id;
    setChatMode(readStoredChatMode(id));
  }, [id]);

  useEffect(() => {
    try {
      localStorage.setItem(chatModeStorageKey(id), chatMode);
    } catch {
      // Ignore write errors (private mode, quota, etc.).
    }
  }, [id, chatMode]);

  return (
    <ChatContext.Provider value={{ messages, setMessages, chatMode, setChatMode }}>
      {children}
    </ChatContext.Provider>
  );
};

// Simple hook to get everything
export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};
